import React from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { StepComponentProps } from "./types";

export const PasswordStep: React.FC<StepComponentProps> = ({
  formData,
  control,
  showPassword = false,
  showConfirmPassword = false,
  onTogglePassword,
  onToggleConfirmPassword,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3">
      <div>
        <label
          className="block font-semibold mb-1 text-primary dark:text-primary text-sm"
          htmlFor="password"
        >
          {t("auth.registration.password")}
        </label>
        <Controller
          name="password"
          control={control}
          rules={{
            required:
              t("auth.registration.passwordRequired") || "Password is required",
            minLength: {
              value: 8,
              message:
                t("auth.registration.passwordMinLength") ||
                "Password must be at least 8 characters",
            },
            pattern: {
              value:
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
              message:
                t("auth.registration.passwordComplexity") ||
                "Password must contain uppercase, lowercase, number, and special character",
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <>
              <div className="relative">
                <input
                  {...field}
                  id="password"
                  type={showPassword ? "text" : "password"}
                  className={`w-full px-3 py-3 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/60 text-sm bg-[var(--bg-surface)] text-[var(--text-primary)] ${
                    error ? "border-red-400" : "border-[var(--border-light)]"
                  }`}
                  placeholder={
                    t("auth.registration.passwordPlaceholder") ||
                    "Enter your password"
                  }
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                  onClick={onTogglePassword}
                >
                  {showPassword ? (
                    <FaEyeSlash className="w-4 h-4" />
                  ) : (
                    <FaEye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {error && (
                <div className="text-red-500 text-xs mt-1">{error.message}</div>
              )}
            </>
          )}
        />
      </div>
      <div>
        <label
          className="block font-semibold mb-1 text-primary dark:text-primary text-sm"
          htmlFor="confirmPassword"
        >
          {t("auth.registration.confirmPassword")}
        </label>
        <Controller
          name="confirmPassword"
          control={control}
          rules={{
            required:
              t("auth.registration.confirmPasswordRequired") ||
              "Confirm password is required",
            validate: (value) =>
              value === formData.password ||
              t("auth.registration.passwordsDoNotMatch") ||
              "Passwords do not match",
          }}
          render={({ field, fieldState: { error } }) => (
            <>
              <div className="relative">
                <input
                  {...field}
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  className={`w-full px-3 py-3 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/60 text-sm bg-[var(--bg-surface)] text-[var(--text-primary)] ${
                    error ? "border-red-400" : "border-[var(--border-light)]"
                  }`}
                  placeholder={
                    t("auth.registration.confirmPasswordPlaceholder") ||
                    "Confirm your password"
                  }
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                  onClick={onToggleConfirmPassword}
                >
                  {showConfirmPassword ? (
                    <FaEyeSlash className="w-4 h-4" />
                  ) : (
                    <FaEye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {error && (
                <div className="text-red-500 text-xs mt-1">{error.message}</div>
              )}
            </>
          )}
        />
      </div>
    </div>
  );
};
